import CardStatus from './CardStatus';
import dayjs from 'dayjs';
import LessonRescheduleForm from './LessonRescheduleForm';
import useLessonRescheduleProposal from '../../../../operations/client/useLessonRescheduleProposal';
import useLessonStatusUpdate from '../../../../operations/client/useLessonStatusUpdate';
import { Button, Card, Flex, IconButton, List, Spacer, Stack, Text } from '@chakra-ui/react';
import { FiX } from 'react-icons/fi';
import { formatLesson, formatRescheduledLesson, getLessonTypeForCode } from '@opanuj/shared/utils';
import { getLessonStatusForUid } from '@opanuj/shared/lessons';
import { Lesson, LessonStatus, LessonType } from '@opanuj/shared/types';
import { toaster } from '../../../../components/ui/toaster';
import { useState } from 'react';
import { useUser } from '../../../../hooks/useUser';

function LessonCard({ lesson }: { lesson: Lesson }) {
	type CardMode = 'view' | 'canceling' | 'rescheduling';

	const [user, userLoading, userError] = useUser()

	const [cardMode, setCardMode] = useState<CardMode>('view');
	const lessonStatusUpdate = useLessonStatusUpdate({
		onSuccess: () => {
			toaster.create({
				title: 'Lekcja została odwołana',
				type: 'success',
				duration: 2500,
			});
		}
	});
	const lessonRescheduleProposal = useLessonRescheduleProposal({
		onSuccess: () => {
			toaster.create({
				title: 'Wysłano propozycje terminów odrobienia',
				type: 'success',
				duration: 2500,
			});
		}
	});

	const handleCancel = () => {
		setCardMode('view')
		return lessonStatusUpdate.mutate(
			{
				callerUid: user!.uid,
				lessonId: lesson.id,
				status: LessonStatus.Cancelled
			},
			{
				onError: (error) => {
					console.error('Failed to cancel lesson', error);
					toaster.create({
						title: 'Nie udało się odwołać lekcji',
						type: 'error',
						duration: 3000,
					});
				},
			},
		);
	};
	const handleReschedule = (options: Lesson[]) => {
		setCardMode('view');
		return lessonRescheduleProposal.mutate(
			{
				callerUid: user!.uid,
				lessonId: lesson.id,
				options: options,
			},
			{
				onError: (error) => {
					console.error('Failed to send reschedule proposal', error);
					toaster.create({
						title: 'Nie udało się wysłać propozycji terminów',
						type: 'error',
						duration: 3000,
					});
				},
			},
		);
	};

	if (userLoading || userError || !user) return null

	const isCancellable =
		getLessonTypeForCode(lesson.code) === LessonType.KI &&
		lesson.status === LessonStatus.Regular &&
		dayjs().isBefore(dayjs(lesson.timestamp * 1000).subtract(1, 'day'));

	return (
        <Card.Root size="sm" m={2}>
            <Card.Header px={3} py={2}>
				<Flex alignItems="center">
					<Text fontSize="md" fontWeight="normal">{formatLesson(lesson)}</Text>
					<Spacer />
					<Stack
						alignItems="center"
						ml={2}
						direction={{ base: 'column', md: 'row' }}
					>
						<CardStatus
							status={getLessonStatusForUid(lesson, user.uid)}
							hasRescheduleOptions={!!lesson.rescheduleOptions}
						/>
						{cardMode === 'view' ? (
							<>
								{lesson.status === LessonStatus.Cancelled && !lesson.rescheduleOptions && (
									<Button
										colorPalette="blue"
										variant="outline"
										onClick={() =>
											setCardMode('rescheduling')
										}
									>
										Umów odrobienie
									</Button>
								)}
								{isCancellable && (
									<Button
										colorPalette="orange"
										variant="outline"
										onClick={() => setCardMode('canceling')}
										loading={lessonStatusUpdate.isLoading}
										disabled={lessonStatusUpdate.isLoading}
									>
										Odwołaj
									</Button>
								)}
							</>
						) : (
							<IconButton
								variant="outline"
								aria-label="close"
								onClick={() => setCardMode('view')}
							>
								<FiX />
							</IconButton>
						)}
					</Stack>
				</Flex>
			</Card.Header>
            {cardMode === 'canceling' && (
				<Card.Body>
					<Flex>
						<Text textStyle="italic">
							Czy na pewno chcesz odwołać tą lekcję?
						</Text>
						<Spacer />
						<Button onClick={handleCancel} colorPalette="yellow">
							Odwołaj
						</Button>
					</Flex>
				</Card.Body>
			)}
            {cardMode === 'rescheduling' && (
				<Card.Body>
					<LessonRescheduleForm
						lesson={lesson}
						onSubmit={handleReschedule}
					/>
				</Card.Body>
			)}
            {lesson.status === LessonStatus.Cancelled && lesson.rescheduleOptions && (
				<Card.Body>
					<Text fontSize='md'>Wysłane propozycje terminów odrobienia zajęć:</Text>
					<List.Root as='ul' listStylePosition={"inside"}>
						{lesson.rescheduleOptions.map((option) => (
							<List.Item key={option.id} mt={2}>
								{formatRescheduledLesson(option)}
							</List.Item>

						))}
					</List.Root>
				</Card.Body>
			)}
        </Card.Root>
    );
}

export default LessonCard;
